/**
 * Save a blob-URL to disk.
 *
 * In Tauri, `<a href="blob:..." download>` is silently dropped by WebView2 —
 * so we detect the Tauri runtime and go through the dialog + fs plugins.
 * Web builds fall back to the anchor-click trick.
 */

type TauriGlobal = {
  dialog?: {
    save: (options: {
      defaultPath?: string;
      filters?: { name: string; extensions: string[] }[];
    }) => Promise<string | null>;
  };
  fs?: {
    writeFile: (path: string, contents: Uint8Array) => Promise<void>;
  };
};

function getTauri(): TauriGlobal | null {
  const w = window as unknown as { __TAURI__?: TauriGlobal };
  return w.__TAURI__ ?? null;
}

const EXT_LABEL: Record<string, string> = {
  wav: 'WAV Audio',
  webm: 'WebM',
  mp3: 'MP3 Audio',
  mp4: 'MP4 Video',
};

export async function saveBlobUrl(url: string, filename: string): Promise<void> {
  const tauri = getTauri();
  if (tauri?.dialog?.save && tauri.fs?.writeFile) {
    const ext = (filename.split('.').pop() || 'webm').toLowerCase();
    const label = EXT_LABEL[ext] ?? ext.toUpperCase();
    const dest = await tauri.dialog.save({
      defaultPath: filename,
      filters: [{ name: label, extensions: [ext] }],
    });
    if (!dest) return; // user cancelled
    const buf = new Uint8Array(await (await fetch(url)).arrayBuffer());
    await tauri.fs.writeFile(dest, buf);
    return;
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
}
